import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';
import { toast } from '../components/Toast';
import './Profile.css';

function Profile() {
  const { user, branchName } = useAuth();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordData, setPasswordData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    } else if (!/[a-z]/.test(passwordData.newPassword) || !/[A-Z]/.test(passwordData.newPassword) || !/[0-9]/.test(passwordData.newPassword) || !/[!@#$%^&*(),.?":{}|<>]/.test(passwordData.newPassword)) {
      newErrors.newPassword = 'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmitPassword = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post('/auth/change-password', {
        newPassword: passwordData.newPassword
      });

      toast.success(response.data.message || 'Password changed successfully');
      
      // Reset form
      setPasswordData({
        newPassword: '',
        confirmPassword: ''
      });
      setShowPasswordForm(false);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to change password';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getRoleDisplayName = (role) => {
    if (!role) return 'N/A';
    return role.replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>My Profile</h1>
      </div>

      {/* User Information Card */}
      <div className="profile-card">
        <div className="profile-card-header">
          <h2>Personal Information</h2>
        </div>
        <div className="profile-card-body">
          <div className="profile-avatar-section">
            <div className="profile-avatar-large">
              {user?.name
                ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                : '?'}
            </div>
            <div className="profile-avatar-info">
              <h3>{user?.name || 'User'}</h3>
              <span className="profile-email">{user?.email || 'N/A'}</span>
            </div>
          </div>

          <div className="profile-info-grid">
            <div className="profile-info-item">
              <label>Role</label>
              <span className="profile-role-badge">
                {user?.customRoleName || getRoleDisplayName(user?.role)}
              </span>
            </div>

            {user?.role !== 'SUPER_ADMIN' && (
              <>
                <div className="profile-info-item">
                  <label>Branch</label>
                  <span>{branchName || 'All Branches'}</span>
                </div>

                <div className="profile-info-item">
                  <label>Company</label>
                  <span>{user?.companyName || 'N/A'}</span>
                </div>
              </>
            )}

            {user?.phone && (
              <div className="profile-info-item">
                <label>Phone</label>
                <span>{user.phone}</span>
              </div>
            )}

            <div className="profile-info-item">
              <label>Account Status</label>
              <span className={`status-badge ${user?.active ? 'status-active' : 'status-inactive'}`}>
                {user?.active ? 'Active' : 'Inactive'}
              </span>
            </div>

            {user?.emailVerified !== undefined && (
              <div className="profile-info-item">
                <label>Email Verified</label>
                <span className={`status-badge ${user?.emailVerified ? 'status-verified' : 'status-unverified'}`}>
                  {user?.emailVerified ? 'Verified' : 'Not Verified'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Security Card */}
      <div className="profile-card">
        <div className="profile-card-header">
          <h2>Security</h2>
          {!showPasswordForm && (
            <button
              className="btn btn-primary"
              onClick={() => setShowPasswordForm(true)}
            >
              Change Password
            </button>
          )}
        </div>
        <div className="profile-card-body">
          {!showPasswordForm ? (
            <div className="profile-security-info">
              <p className="profile-security-text">
                <span className="profile-security-icon">🔒</span>
                Your password is securely encrypted. Click "Change Password" to update it.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmitPassword} className="profile-password-form">
              <div className="form-group">
                <label htmlFor="newPassword">New Password *</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className={errors.newPassword ? 'input-error' : ''}
                  placeholder="Enter new password (minimum 8 characters)"
                  disabled={loading}
                />
                {errors.newPassword && (
                  <span className="error-message">{errors.newPassword}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm Password *</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className={errors.confirmPassword ? 'input-error' : ''}
                  placeholder="Re-enter new password"
                  disabled={loading}
                />
                {errors.confirmPassword && (
                  <span className="error-message">{errors.confirmPassword}</span>
                )}
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordData({ newPassword: '', confirmPassword: '' });
                    setErrors({});
                  }}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default Profile;
